import { state } from './state.js';
import * as UI from './ui.js';
import * as API from './api.js';
import { UploadQueue } from './upload_queue.js';
import { refreshSelectedLibraryPreview } from './library_preview.js';
import { TOASTS } from './config/toasts.js';
import { UI_COPY } from './config/uiCopy.js';

const uploadManager = new UploadQueue();

function _reasonLabel(entry) {
    const reason = String(entry.reason || '').trim();
    if (reason === 'type') return 'Unsupported file type';
    if (reason === 'size') return 'File too large';
    if (reason === 'pixels') return 'Image too large';
    if (reason === 'duplicate') return 'Duplicate photo';

    const raw = String(entry.message || '').trim() || 'Unknown error';
    const marker = ' Try again in ';
    const cleaned = raw.includes(marker) ? raw.split(marker)[0].trim() : raw;
    return cleaned || 'Unknown error';
}

function summarizeFailures(failures = [], kind = 'error', maxFiles = 6) {
    const groups = new Map();
    (failures || []).forEach((entry) => {
        if (!entry || entry.kind !== kind) return;
        const label = _reasonLabel(entry);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(entry.file || 'unknown');
    });

    const lines = [];
    for (const [label, files] of groups.entries()) {
        const visible = files.slice(0, maxFiles);
        visible.forEach((f) => lines.push(`- ${f}`));
        if (files.length > maxFiles) {
            lines.push(`- and ${files.length - maxFiles} more`);
        }
        lines.push(`Reason: ${label}`);
        lines.push('');
    }
    while (lines.length && !lines[lines.length - 1].trim()) {
        lines.pop();
    }
    return lines;
}

export function addFilesToQueue(files) {
    if (!state.selectedLibraryId) {
        const toast = TOASTS.noLibrarySelected;
        UI.showToast(toast.title, toast.message, toast.duration);
        return;
    }
    uploadManager.addFiles(files, state.selectedLibraryId);
}

export async function traverseFileTree(item) {
    if (item.isFile) {
        await new Promise(r => item.file(f => {
            addFilesToQueue([f]);
            r();
        }));
    } else if (item.isDirectory) {
        const reader = item.createReader();
        const readEntries = async () => new Promise(resolve => {
            reader.readEntries(async entries => {
                for(const e of entries) await traverseFileTree(e);
                resolve();
            });
        });
        await readEntries();
    }
}

export async function startLibraryUpload() {
    uploadManager.onProgress = (current, total) => {
        UI.updateUploadProgress(current, total);
    };

    uploadManager.onToast = (title, msg, type) => {
        UI.showToast(title, msg, type || 'info');
    };

    uploadManager.onComplete = async (stats) => {
        UI.updateUploadProgress(0, 0);

        const previewData = await refreshSelectedLibraryPreview();
        if (state.selectedLibraryId && previewData) {
            const idx = (state.libraries || []).findIndex((lib) => lib.id === state.selectedLibraryId);
            if (idx >= 0) {
                const updated = {
                    ...state.libraries[idx],
                    tile_count: Number(previewData.count || state.libraries[idx].tile_count || 0),
                    previews: Array.isArray(previewData.images) ? previewData.images : (state.libraries[idx].previews || []),
                };
                state.libraries[idx] = updated;
                if (typeof UI.updateLibraryStripItem === 'function') {
                    UI.updateLibraryStripItem(updated);
                }
                document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: updated } }));
            }
        }

        const added = Number.isFinite(stats.completed)
            ? stats.completed
            : (stats.total - stats.skipped - stats.errors);
        if (state.selectedLibraryId && !previewData && added > 0) {
            const idx = (state.libraries || []).findIndex((lib) => lib.id === state.selectedLibraryId);
            if (idx >= 0) {
                const currentCount = Number(state.libraries[idx].tile_count || 0);
                const updated = {
                    ...state.libraries[idx],
                    tile_count: currentCount + added,
                };
                state.libraries[idx] = updated;
                if (typeof UI.updateLibraryStripItem === 'function') {
                    UI.updateLibraryStripItem(updated);
                }
                document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: updated } }));
            }
        }
        let msg = UI_COPY.uploads.uploadSummary(added, stats.skipped, stats.errors);

        const errorSummary = summarizeFailures(stats.error_details || [], 'error');
        if (stats.errors > 0 && errorSummary.length) {
            msg += `\nErrors:\n${errorSummary.join('\n')}`;
        }

        const skippedSummary = summarizeFailures(stats.error_details || [], 'skipped');
        if (stats.skipped > 0 && skippedSummary.length) {
            msg += `\nSkipped:\n${skippedSummary.join('\n')}`;
        }

        const isPersistent = (stats.errors > 0 || stats.skipped > 0);
        const hasChanges = added > 0;
        if (stats.abort_reason === 'limit') {
            API.logUiFlow('library_upload', 'batch_complete', 'limit_reached', {
                completed: Number(stats.completed || 0),
                skipped: Number(stats.skipped || 0),
                errors: Number(stats.errors || 0),
            }).catch(() => {});
        } else if (stats.cancelled) {
            UI.showToast('Upload stopped', msg, isPersistent ? 0 : 3000);
            API.logUiFlow('library_upload', 'batch_complete', 'cancelled', {
                completed: Number(stats.completed || 0),
                skipped: Number(stats.skipped || 0),
                errors: Number(stats.errors || 0),
            }).catch(() => {});
        } else if (!hasChanges) {
            UI.showToast('No photos uploaded', msg, isPersistent ? 0 : 3000);
            API.logUiFlow('library_upload', 'batch_complete', 'no_change', {
                completed: Number(stats.completed || 0),
                skipped: Number(stats.skipped || 0),
                errors: Number(stats.errors || 0),
            }).catch(() => {});
        } else {
            const toast = TOASTS.uploadComplete(msg, isPersistent);
            UI.showToast(toast.title, toast.message, toast.duration);
            API.logUiFlow('library_upload', 'batch_complete', stats.errors > 0 ? 'partial' : 'ok', {
                completed: Number(stats.completed || 0),
                skipped: Number(stats.skipped || 0),
                errors: Number(stats.errors || 0),
            }).catch(() => {});
        }

        document.dispatchEvent(new CustomEvent('library:upload:complete', {
            detail: {
                library_id: String(state.selectedLibraryId || ''),
                added: Number(added || 0),
                completed: Number(stats.completed || 0),
                skipped: Number(stats.skipped || 0),
                errors: Number(stats.errors || 0),
                cancelled: Boolean(stats.cancelled),
            },
        }));

        // Avoid full library strip refresh here to prevent thumbnail refetch churn.
    };

    uploadManager.start();
}

export function stopLibraryUpload() {
    uploadManager.cancelBatch('user', 'Upload cancelled.');
}
